import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { API_BASE_URL } from '../config/api';
import { Shield, Filter, ChevronDown, ChevronUp, Clock, User, Package, Settings, RefreshCw, ShoppingBag } from 'lucide-react';

interface AuditLog {
  id: number;
  store_id: number;
  employee_id: number | null;
  employee_name: string;
  action: string;
  entity_type: string;
  entity_id: number | null;
  old_values: any;
  new_values: any;
  ip_address: string | null;
  created_at: string;
}

interface Filters {
  action: string;
  entity_type: string;
  employee_id: string;
  from: string;
  to: string;
}

const ENTITY_ICONS: Record<string, any> = {
  product: Package,
  store: Settings,
  employee: User,
  settings: Settings,
  order: ShoppingBag,
  fiscal_document: ShoppingBag,
};

const AuditLogViewer: React.FC = () => {
  const { t } = useTranslation(['analytics', 'common']);
  const { store } = useAuth();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    action: '',
    entity_type: '',
    employee_id: '',
    from: '',
    to: ''
  });
  const [filterOptions, setFilterOptions] = useState<{
    actions: string[];
    entityTypes: string[];
    employees: { employee_id: number; name: string }[];
  }>({ actions: [], entityTypes: [], employees: [] });

  const LIMIT = 50;

  const ACTION_LABELS: Record<string, { label: string; color: string }> = {
    create: { label: t('analytics:audit.actionCreate'), color: 'bg-green-500/20 text-green-600 dark:text-green-400' },
    update: { label: t('analytics:audit.actionUpdate'), color: 'bg-blue-500/20 text-blue-600 dark:text-blue-400' },
    delete: { label: t('analytics:audit.actionDelete'), color: 'bg-red-500/20 text-red-600 dark:text-red-400' },
    login: { label: t('analytics:audit.actionLogin'), color: 'bg-purple-500/20 text-purple-600 dark:text-purple-400' },
    export: { label: t('analytics:audit.actionExport'), color: 'bg-amber-500/20 text-amber-700 dark:text-amber-400' },
    import: { label: t('analytics:audit.actionImport'), color: 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400' },
    // Order lifecycle actions (added with the order audit trail).
    edit_items: { label: 'Αλλαγή προϊόντων', color: 'bg-orange-500/20 text-orange-600 dark:text-orange-400' },
    complete: { label: 'Ολοκλήρωση / Πληρωμή', color: 'bg-green-500/20 text-green-600 dark:text-green-400' },
    cancel: { label: 'Ακύρωση', color: 'bg-red-500/20 text-red-600 dark:text-red-400' },
    dismiss: { label: 'Αφαίρεση (πλατφόρμα)', color: 'bg-orange-500/20 text-orange-600 dark:text-orange-400' },
  };

  const fetchLogs = useCallback(async () => {
    if (!store?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(LIMIT));
      params.set('offset', String(page * LIMIT));
      if (filters.action) params.set('action', filters.action);
      if (filters.entity_type) params.set('entity_type', filters.entity_type);
      if (filters.employee_id) params.set('employee_id', filters.employee_id);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);

      const res = await fetch(
        `${API_BASE_URL}/api/stores/${store.id}/audit-logs?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [store?.id, page, filters]);

  const fetchFilterOptions = useCallback(async () => {
    if (!store?.id) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/stores/${store.id}/audit-logs/filters`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` } }
      );
      if (res.ok) {
        setFilterOptions(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  }, [store?.id]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchFilterOptions(); }, [fetchFilterOptions]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' });
  };

  const getChangedFields = (oldVals: any, newVals: any): { field: string; from: any; to: any }[] => {
    if (!oldVals || !newVals) return [];
    const changes: { field: string; from: any; to: any }[] = [];
    const skipFields = ['updated_at', 'created_at', 'password_hash'];

    for (const key of Object.keys(newVals)) {
      if (skipFields.includes(key)) continue;
      const oldVal = oldVals[key];
      const newVal = newVals[key];
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({ field: key, from: oldVal, to: newVal });
      }
    }
    return changes;
  };

  const renderValue = (val: any): string => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? t('common:yes') : t('common:no');
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center">
            <Shield className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-t1">{t('analytics:audit.changeHistory')}</h2>
            <p className="text-xs text-t3">{t('analytics:audit.entriesCount', { count: total })}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-all active:scale-95 ${
              showFilters ? 'bg-indigo-600 text-white' : 'bg-surface-2 text-t2 hover:bg-surface-3'
            }`}
          >
            <Filter className="h-4 w-4" />
            {t('analytics:audit.filters')}
          </button>
          <button
            onClick={() => { setPage(0); fetchLogs(); }}
            className="flex items-center gap-2 px-3 py-2 bg-surface-2 hover:bg-surface-3 rounded-xl text-sm text-t2 transition-all active:scale-95"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-surface-2 dark:bg-surface-1 rounded-xl border border-line p-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <select
              value={filters.action}
              onChange={e => { setFilters(f => ({ ...f, action: e.target.value })); setPage(0); }}
              className="px-3 py-2 bg-surface-2 border border-line-strong rounded-lg text-sm text-t1 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            >
              <option value="">{t('analytics:audit.allActions')}</option>
              {filterOptions.actions.map(a => (
                <option key={a} value={a}>{ACTION_LABELS[a]?.label || a}</option>
              ))}
            </select>

            <select
              value={filters.entity_type}
              onChange={e => { setFilters(f => ({ ...f, entity_type: e.target.value })); setPage(0); }}
              className="px-3 py-2 bg-surface-2 border border-line-strong rounded-lg text-sm text-t1 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            >
              <option value="">{t('analytics:audit.allTypes')}</option>
              {filterOptions.entityTypes.map(t2 => (
                <option key={t2} value={t2}>{t2}</option>
              ))}
            </select>

            <select
              value={filters.employee_id}
              onChange={e => { setFilters(f => ({ ...f, employee_id: e.target.value })); setPage(0); }}
              className="px-3 py-2 bg-surface-2 border border-line-strong rounded-lg text-sm text-t1 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            >
              <option value="">{t('analytics:audit.allUsers')}</option>
              {filterOptions.employees.map(e => (
                <option key={e.employee_id} value={e.employee_id}>{e.name}</option>
              ))}
            </select>

            <input
              type="date"
              value={filters.from}
              onChange={e => { setFilters(f => ({ ...f, from: e.target.value })); setPage(0); }}
              className="px-3 py-2 bg-surface-2 border border-line-strong rounded-lg text-sm text-t1 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              placeholder={t('analytics:startDate')}
            />

            <input
              type="date"
              value={filters.to}
              onChange={e => { setFilters(f => ({ ...f, to: e.target.value })); setPage(0); }}
              className="px-3 py-2 bg-surface-2 border border-line-strong rounded-lg text-sm text-t1 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              placeholder={t('analytics:endDate')}
            />
          </div>
          {(filters.action || filters.entity_type || filters.employee_id || filters.from || filters.to) && (
            <button
              onClick={() => { setFilters({ action: '', entity_type: '', employee_id: '', from: '', to: '' }); setPage(0); }}
              className="mt-2 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
            >
              {t('analytics:audit.clearFilters')}
            </button>
          )}
        </div>
      )}

      {/* Log Entries */}
      <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
        {loading && logs.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="h-6 w-6 animate-spin text-t3" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-t3">
            <Shield className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p>{t('analytics:audit.noEntries')}</p>
          </div>
        ) : (
          <div className="divide-y divide-line/50">
            {logs.map(log => {
              const isExpanded = expandedId === log.id;
              const actionInfo = ACTION_LABELS[log.action] || { label: log.action, color: 'bg-surface-2 text-t2' };
              const EntityIcon = ENTITY_ICONS[log.entity_type] || Settings;
              const changes = isExpanded ? getChangedFields(log.old_values, log.new_values) : [];

              return (
                <div key={log.id}>
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : log.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-3 dark:hover:bg-surface-2/30 transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-surface-3 dark:bg-surface-2/50 flex items-center justify-center flex-shrink-0">
                      <EntityIcon className="h-4 w-4 text-t3" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${actionInfo.color}`}>
                          {actionInfo.label}
                        </span>
                        <span className="text-sm text-t1 font-medium">{log.entity_type}</span>
                        {log.entity_id && (
                          <span className="text-xs text-t4">#{log.entity_id}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-t3">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {log.employee_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(log.created_at)}
                        </span>
                      </div>
                    </div>
                    {(log.old_values || log.new_values) && (
                      isExpanded ? <ChevronUp className="h-4 w-4 text-t4" /> : <ChevronDown className="h-4 w-4 text-t4" />
                    )}
                  </button>

                  {isExpanded && (log.old_values || log.new_values) && (
                    <div className="px-4 pb-3 pl-15">
                      {log.action === 'create' && log.new_values ? (
                        <div className="bg-surface-0/50 rounded-lg p-3 text-xs">
                          <p className="text-t3 mb-2 font-medium">{t('analytics:audit.newValues')}</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                            {Object.entries(log.new_values)
                              .filter(([k]) => !['updated_at', 'created_at', 'password_hash'].includes(k))
                              .slice(0, 15)
                              .map(([k, v]) => (
                                <div key={k}>
                                  <span className="text-t4">{k}: </span>
                                  <span className="text-green-600 dark:text-green-400">{renderValue(v)}</span>
                                </div>
                              ))}
                          </div>
                        </div>
                      ) : changes.length > 0 ? (
                        <div className="bg-surface-0/50 rounded-lg overflow-hidden text-xs">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-line">
                                <th className="text-left px-3 py-1.5 text-t3 font-medium">{t('analytics:audit.fieldColumn')}</th>
                                <th className="text-left px-3 py-1.5 text-t3 font-medium">{t('analytics:audit.beforeColumn')}</th>
                                <th className="text-left px-3 py-1.5 text-t3 font-medium">{t('analytics:audit.afterColumn')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {changes.map(c => (
                                <tr key={c.field} className="border-b border-line/50">
                                  <td className="px-3 py-1.5 text-t2 font-medium">{c.field}</td>
                                  <td className="px-3 py-1.5 text-red-600 dark:text-red-400">{renderValue(c.from)}</td>
                                  <td className="px-3 py-1.5 text-green-600 dark:text-green-400">{renderValue(c.to)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-xs text-t4 pl-3">{t('analytics:audit.noChangesDetected')}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-line">
            <p className="text-xs text-t3">
              {t('analytics:audit.pageOf', { current: page + 1, total: totalPages })}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 bg-surface-2 rounded-lg text-xs text-t2 disabled:opacity-30 hover:bg-surface-3 transition-all"
              >
                {t('analytics:audit.previousPage')}
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 bg-surface-2 rounded-lg text-xs text-t2 disabled:opacity-30 hover:bg-surface-3 transition-all"
              >
                {t('analytics:audit.nextPage')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogViewer;
